'use client';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { deriveCameraGraphNavigationPhase } from './_pure/cameraGraph.js';
import { deriveCameraInitializationPhase } from './_pure/cameraInitialization.js';
import { phaseControlsCamera } from './_pure/cameraNavigation.js';
import { deriveTransitionSignature, normalizeFrameAgainstTopology, validateTransitionPlan } from './_pure/transitionEngine.js';
import { playTransitionPlan } from './transitionPlayback.js';
/**
 * Paused updates freeze the existing handle before the next animation frame.
 * Changing resetKey cancels and replaces the plan even when its signature is unchanged.
 */
export function useTransitionPlayback(output, camera, onComplete, onPlaybackActivityChange, paused = false, resetKey = 0, onPhaseComplete, clock) {
    const base = useMemo(() => normalizeFrameAgainstTopology(output.base), [output.base]);
    const phases = useMemo(() => output.transitionPhases ?? [], [output.transitionPhases]);
    const plan = useMemo(() => {
        validateTransitionPlan(base, phases);
        return { base, phases };
    }, [base, phases]);
    const signature = useMemo(() => deriveTransitionSignature(base, phases), [base, phases]);
    if (camera === undefined && phases.some(phaseControlsCamera)) {
        throw new Error('Camera transitions require a camera controller.');
    }
    const planRef = useRef(plan);
    planRef.current = plan;
    const completionRef = useRef(onComplete);
    completionRef.current = onComplete;
    const phaseCompletionRef = useRef(onPhaseComplete);
    phaseCompletionRef.current = onPhaseComplete;
    const activityRef = useRef(onPlaybackActivityChange);
    activityRef.current = onPlaybackActivityChange;
    const playbackRef = useRef(undefined);
    const pausedRef = useRef(paused);
    pausedRef.current = paused;
    const resetKeyRef = useRef(resetKey);
    resetKeyRef.current = resetKey;
    const initializedKeyRef = useRef(undefined);
    const arrivedViewRef = useRef(undefined);
    const stationStateRef = useRef({});
    const travelStateRef = useRef({});
    const arrivedGraphSignatureRef = useRef(undefined);
    const initializationKey = base.spaceRenderOptions?.camera?.initializationKey;
    const navigationMode = base.spaceRenderOptions?.camera?.navigationMode;
    const activeView = base.spaceRenderOptions?.camera?.activeView;
    const navigationDurationMs = base.spaceRenderOptions?.camera?.navigationDurationMs;
    const navigationOrbitSignature = JSON.stringify(base.spaceRenderOptions?.camera?.navigationOrbitCenter);
    const cameraGraph = base.spaceRenderOptions?.camera?.graph;
    const cameraGraphSignature = useMemo(() => JSON.stringify(cameraGraph), [cameraGraph]);
    const [sample, setSample] = useState({ frame: base, signature, resetKey });
    const frameRef = useRef(base);
    const publishFrame = useCallback((next) => {
        frameRef.current = next;
        setSample({ frame: next, signature, resetKey });
    }, [signature, resetKey]);
    useEffect(() => {
        const current = planRef.current;
        if (camera === null)
            return;
        const initialization = camera ? deriveCameraInitializationPhase(current.base, initializedKeyRef.current) : undefined;
        const declaration = current.base.spaceRenderOptions?.camera;
        const needsStationResolution = current.phases.length > 0 && (stationStateRef.current.current?.aspect !== (camera?.viewportAspect?.() ?? 1)
            || arrivedGraphSignatureRef.current !== cameraGraphSignature);
        const arrived = initialization ? 'overview' : declaration?.navigationMode !== 'guided' || needsStationResolution
            ? undefined : arrivedViewRef.current;
        const hasArrival = current.phases.some((phase) => phase.cameraNavigation?.arrival === 'reuse');
        const navigation = camera && !hasArrival
            ? deriveCameraGraphNavigationPhase(current.base, arrived) : undefined;
        if (declaration?.navigationMode !== 'guided')
            arrivedViewRef.current = undefined;
        if (declaration?.navigationMode !== 'guided' || initialization)
            delete stationStateRef.current.current;
        const playbackPhases = [...(initialization ? [initialization] : []), ...(navigation ? [navigation] : []), ...current.phases];
        activityRef.current?.(playbackPhases.length > 0);
        const handle = playTransitionPlan({
            ...current,
            previousFrame: frameRef.current,
            phases: playbackPhases,
            ...(camera ? { camera } : {}),
            stationState: stationStateRef.current,
            travelState: travelStateRef.current,
            paused: pausedRef.current,
            clock: clock ?? {
                now: () => performance.now(),
                requestFrame: (callback) => window.requestAnimationFrame(callback),
                cancelFrame: (handle) => window.cancelAnimationFrame(handle),
                delay: (callback, duration) => window.setTimeout(callback, duration),
                cancelDelay: (handle) => window.clearTimeout(handle),
            },
            onFrame: publishFrame,
            onPhaseComplete: (phase) => {
                if (resetKeyRef.current !== resetKey)
                    return;
                if (phase === initialization)
                    initializedKeyRef.current = initializationKey;
                const request = phase.cameraNavigation;
                if (request?.view)
                    arrivedViewRef.current = request.view;
                else if (request?.path)
                    arrivedViewRef.current = declaration?.graph?.paths.find((path) => path.pathId === request.path)?.to;
                if (request)
                    arrivedGraphSignatureRef.current = cameraGraphSignature;
                const key = phase.transitionOptions.transitionKey;
                if (key !== undefined)
                    phaseCompletionRef.current?.(key);
            },
            onComplete: () => {
                if (resetKeyRef.current !== resetKey)
                    return;
                activityRef.current?.(false);
                if (current.phases.length > 0)
                    completionRef.current?.();
            },
        });
        playbackRef.current = handle;
        return () => {
            handle();
            if (playbackRef.current === handle)
                playbackRef.current = undefined;
            activityRef.current?.(false);
        };
    }, [signature, camera, initializationKey, navigationMode, activeView, navigationDurationMs, navigationOrbitSignature, cameraGraphSignature, publishFrame, resetKey, clock]);
    useLayoutEffect(() => {
        if (paused)
            playbackRef.current?.pause();
        else
            playbackRef.current?.resume();
    }, [paused]);
    // Guidance already belongs to the new output, even before its playback effect can publish.
    return sample.signature === signature && sample.resetKey === resetKey ? sample.frame : base;
}
